import React, { useState } from 'react';
import { useEditor, Element } from '@craftjs/core';
import { Paper, Typography, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import * as Icons from '@material-ui/icons';

import { Text }                         from './Text';
import { Button }                       from './Button';
import { Container }                    from './Container';
import { Image }                        from './Image';
import { Input }                        from './Input';
import { Form }                         from './Form';
import { Grid as CustomGrid, GRID_PRESETS }  from './Grid';
import { Card }                         from './Card';
import { Divider }                      from './Divider';
import { ChannelingCard, PRESETS }                    from './ChannelingCard';
import { SearchBox, SEARCH_PRESETS }                  from './SearchBox';
import { ChannelingDetailBlock, DETAIL_PRESETS }      from './ChannelingDetailBlock';
import { DynamicField }                               from './DynamicField';
import { getContentType }                             from '../config/contentTypes';

const useStyles = makeStyles((theme) => ({
  toolbox: {
    height: '100%',
    overflow: 'auto',
    backgroundColor: '#1e1e2e',
    display: 'flex',
    flexDirection: 'column',
    userSelect: 'none',
    borderRadius: 0,
  },
  header: {
    padding: theme.spacing(1.5, 2),
    borderBottom: '1px solid rgba(255,255,255,0.07)',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  headerTitle: {
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.12em',
    color: 'rgba(255,255,255,0.4)',
    textTransform: 'uppercase',
  },
  categoryHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0.8, 2),
    cursor: 'pointer',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.04)',
    },
  },
  categoryLabel: {
    fontSize: '0.62rem',
    fontWeight: 700,
    letterSpacing: '0.1em',
    color: 'rgba(255,255,255,0.3)',
    textTransform: 'uppercase',
  },
  chevron: {
    fontSize: '0.9rem',
    color: 'rgba(255,255,255,0.2)',
    transition: 'transform 0.2s ease',
  },
  chevronOpen: {
    transform: 'rotate(180deg)',
  },
  itemGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 4,
    padding: theme.spacing(1),
    backgroundColor: 'rgba(0,0,0,0.12)',
  },
  toolItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    padding: theme.spacing(1.2, 0.5),
    cursor: 'grab',
    borderRadius: 6,
    border: '1px solid transparent',
    backgroundColor: 'rgba(255,255,255,0.03)',
    transition: 'all 0.15s ease',
    position: 'relative',
    overflow: 'hidden',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.08)',
      border: '1px solid rgba(255,255,255,0.1)',
    },
    '&:active': {
      cursor: 'grabbing',
      backgroundColor: 'rgba(255,255,255,0.14)',
    },
  },
  presetDot: {
    position: 'absolute',
    top: 4,
    right: 4,
    width: 5,
    height: 5,
    borderRadius: '50%',
  },
  toolLabel: {
    fontSize: '0.58rem',
    color: 'rgba(255,255,255,0.4)',
    textAlign: 'center',
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '100%',
    paddingLeft: 2,
    paddingRight: 2,
  },
  hint: {
    padding: theme.spacing(1.5, 2),
    marginTop: 'auto',
    borderTop: '1px solid rgba(255,255,255,0.05)',
  },
  hintText: {
    fontSize: '0.58rem',
    color: 'rgba(255,255,255,0.18)',
    textAlign: 'center',
  },
}));

// ── Categoría colapsable ─────────────────────────────────────────────────────
const Category = ({ label, children, defaultOpen = true }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div>
      <div className={classes.categoryHeader} onClick={() => setOpen((o) => !o)}>
        <Typography className={classes.categoryLabel}>{label}</Typography>
        <Icons.ExpandMore
          className={`${classes.chevron} ${open ? classes.chevronOpen : ''}`}
        />
      </div>
      {open && <div className={classes.itemGrid}>{children}</div>}
    </div>
  );
};

// ── Ítem individual ──────────────────────────────────────────────────────────
const ToolItem = ({ connectorRef, icon, label, accentColor }) => {
  const classes = useStyles();

  return (
    <Tooltip title={label} placement="top" enterDelay={500}>
      <div ref={connectorRef} className={classes.toolItem}>
        {accentColor && (
          <span className={classes.presetDot} style={{ backgroundColor: accentColor }} />
        )}
        <span
          className="material-icons"
          style={{
            fontSize: '1.2rem',
            color: accentColor || 'rgba(255,255,255,0.5)',
          }}
        >
          {icon}
        </span>
        <span className={classes.toolLabel}>{label}</span>
      </div>
    </Tooltip>
  );
};

// ── Toolbox ──────────────────────────────────────────────────────────────────
export const Toolbox = ({ pageType, contentType }) => {
  const classes = useStyles();
  const { connectors } = useEditor();
  const isDynamic = pageType === 'dynamic_list' || pageType === 'dynamic_detail';
  const contentTypeDef = isDynamic ? getContentType(contentType) : null;

  return (
    <Paper className={classes.toolbox} elevation={0} square>

      <div className={classes.header}>
        <Icons.Dashboard style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.25)' }} />
        <Typography className={classes.headerTitle}>Componentes</Typography>
      </div>

      <Category label="Layout">
        <ToolItem
          connectorRef={(ref) => connectors.create(ref, <Element is={Container} canvas />)}
          icon="crop_square"
          label="Contenedor"
        />
        <ToolItem
          connectorRef={(ref) => connectors.create(ref, <CustomGrid {...GRID_PRESETS.twoCol} />)}
          icon="view_column"
          label="2 Col"
          accentColor="#2196f3"
        />
        <ToolItem
          connectorRef={(ref) => connectors.create(ref, <CustomGrid {...GRID_PRESETS.threeCol} />)}
          icon="view_module"
          label="3 Col"
          accentColor="#2196f3"
        />
        <ToolItem
          connectorRef={(ref) => connectors.create(ref, <CustomGrid {...GRID_PRESETS.autoFill} />)}
          icon="auto_awesome_mosaic"
          label="Auto"
          accentColor="#00bcd4"
        />
        <ToolItem
          connectorRef={(ref) => connectors.create(ref, <CustomGrid {...GRID_PRESETS.asymmetric} />)}
          icon="view_sidebar"
          label="Asimétrico"
          accentColor="#9c27b0"
        />
        <ToolItem
          connectorRef={(ref) => connectors.create(ref, <Card />)}
          icon="credit_card"
          label="Tarjeta"
        />
        <ToolItem
          connectorRef={(ref) => connectors.create(ref, <Divider />)}
          icon="horizontal_rule"
          label="Separador"
        />
      </Category>

      <Category label="Contenido">
        <ToolItem
          connectorRef={(ref) => connectors.create(ref, <Text />)}
          icon="title"
          label="Texto"
        />
        <ToolItem
          connectorRef={(ref) => connectors.create(ref, <Button />)}
          icon="smart_button"
          label="Botón"
        />
        <ToolItem
          connectorRef={(ref) => connectors.create(ref, <Image />)}
          icon="image"
          label="Imagen"
        />
      </Category>

      <Category label="Formularios" defaultOpen={false}>
        <ToolItem
          connectorRef={(ref) => connectors.create(ref, <Element is={Form} canvas />)}
          icon="assignment"
          label="Formulario"
        />
        <ToolItem
          connectorRef={(ref) => connectors.create(ref, <Input />)}
          icon="input"
          label="Campo"
        />
      </Category>

      <Category label="Cards">
        <ToolItem
          connectorRef={(ref) => connectors.create(ref, <ChannelingCard {...PRESETS.dark} />)}
          icon="view_agenda"
          label="Oscura"
          accentColor="#667eea"
        />
        <ToolItem
          connectorRef={(ref) => connectors.create(ref, <ChannelingCard {...PRESETS.vibrant} />)}
          icon="view_agenda"
          label="Vibrante"
          accentColor="#f5576c"
        />
        <ToolItem
          connectorRef={(ref) => connectors.create(ref, <ChannelingCard {...PRESETS.nature} />)}
          icon="view_agenda"
          label="Natural"
          accentColor="#43e97b"
        />
        <ToolItem
          connectorRef={(ref) => connectors.create(ref, <ChannelingCard {...PRESETS.light} />)}
          icon="view_agenda"
          label="Clara"
          accentColor="#3f51b5"
        />
      </Category>

      <Category label="Buscadores">
        <ToolItem
          connectorRef={(ref) => connectors.create(ref, <SearchBox {...SEARCH_PRESETS.dark} />)}
          icon="search"
          label="Oscuro"
          accentColor="#667eea"
        />
        <ToolItem
          connectorRef={(ref) => connectors.create(ref, <SearchBox {...SEARCH_PRESETS.light} />)}
          icon="search"
          label="Claro"
          accentColor="#3f51b5"
        />
        <ToolItem
          connectorRef={(ref) => connectors.create(ref, <SearchBox {...SEARCH_PRESETS.filled} />)}
          icon="search"
          label="Relleno"
          accentColor="#9e9e9e"
        />
        <ToolItem
          connectorRef={(ref) => connectors.create(ref, <SearchBox {...SEARCH_PRESETS.vibrant} />)}
          icon="search"
          label="Vibrante"
          accentColor="#ff5722"
        />
        <ToolItem
          connectorRef={(ref) => connectors.create(ref, <SearchBox {...SEARCH_PRESETS.minimal} />)}
          icon="search"
          label="Minimal"
          accentColor="#aaaaaa"
        />
      </Category>

      <Category label="Detalle Canalización" defaultOpen={false}>
        <ToolItem
          connectorRef={(ref) => connectors.create(ref, <ChannelingDetailBlock {...DETAIL_PRESETS.dark} />)}
          icon="auto_stories"
          label="Oscuro"
          accentColor="#ff5722"
        />
        <ToolItem
          connectorRef={(ref) => connectors.create(ref, <ChannelingDetailBlock {...DETAIL_PRESETS.light} />)}
          icon="auto_stories"
          label="Claro"
          accentColor="#3f51b5"
        />
      </Category>

      {isDynamic && contentTypeDef && (
        <Category label={`Campos · ${contentTypeDef.label}`} defaultOpen={true}>
          {contentTypeDef.fields.map((field) => (
            <ToolItem
              key={field.key}
              connectorRef={(ref) =>
                connectors.create(
                  ref,
                  <DynamicField
                    fieldKey={field.key}
                    fieldLabel={field.label}
                    fieldType={field.type}
                  />
                )
              }
              icon={field.icon}
              label={field.label}
              accentColor="#9c27b0"
            />
          ))}
        </Category>
      )}

      <div className={classes.hint}>
        <Typography className={classes.hintText}>
          Arrastra al canvas
        </Typography>
      </div>

    </Paper>
  );
};